import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Trash2, MapPin, Plus, Maximize2, X, Layers, Move,
  PlusCircle, ArrowRight, AlertCircle, AlertTriangle, Warehouse,
  MoreVertical, ArrowUp, ArrowDown, ZoomIn, ZoomOut, Minimize2
} from 'lucide-react';
import { authFetch } from "../utils/authFetch";

const WarehouseCanvas = ({
  token,
  API_URL,
  zonas,
  setZonas,
  snapToGrid,
  maxZIndex,
  setMaxZIndex,
  almacenSeleccionado,
  refrescarVista,
  setShowCreateLineaModal,
  setNewLinea,
  newLinea
}) => {
  const canvasRef = useRef(null);
  const gridSize = 5;

  // Estados locales
  const [selectedElement, setSelectedElement] = useState(null);
  const [dragState, setDragState] = useState({
    isDragging: false,
    isResizing: false,
    element: null,
    elementType: null,
    parentId: null,
    lineaId: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    offsetX: 0,
    offsetY: 0,
    resizeHandle: null
  });

  // Estados de productos
  const [draggingProduct, setDraggingProduct] = useState(null);
  const [dropTargetNivel, setDropTargetNivel] = useState(null);

  // Estados de modales
  const [showColumnaModal, setShowColumnaModal] = useState(false);
  const [showMoveProductModal, setShowMoveProductModal] = useState(false);
  const [columnaInfo, setColumnaInfo] = useState(null);
  const [moveProductData, setMoveProductData] = useState(null);

  // Estado para menú contextual de zona
  const [zoneMenuOpen, setZoneMenuOpen] = useState(null);

  // Estados de zoom y pan
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const wasPanningRef = useRef(false);
  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.1;

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((screenX, screenY) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (screenX - rect.left - pan.x) / zoom,
      y: (screenY - rect.top - pan.y) / zoom
    };
  }, [zoom, pan]);

  // Zoom handlers
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom + delta));

    // Zoom toward mouse position
    const scale = newZoom / zoom;
    const newPanX = mouseX - (mouseX - pan.x) * scale;
    const newPanY = mouseY - (mouseY - pan.y) * scale;

    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  }, [zoom, pan]);

  const zoomIn = () => {
    const newZoom = Math.min(MAX_ZOOM, zoom + ZOOM_STEP);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const scale = newZoom / zoom;
      setPan({ x: centerX - (centerX - pan.x) * scale, y: centerY - (centerY - pan.y) * scale });
    }
    setZoom(newZoom);
  };

  const zoomOut = () => {
    const newZoom = Math.max(MIN_ZOOM, zoom - ZOOM_STEP);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const scale = newZoom / zoom;
      setPan({ x: centerX - (centerX - pan.x) * scale, y: centerY - (centerY - pan.y) * scale });
    }
    setZoom(newZoom);
  };

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Attach wheel handler with passive: false to allow preventDefault
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const fitToContent = () => {
    if (zonas.length === 0 || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    zonas.forEach(z => {
      minX = Math.min(minX, z.x);
      minY = Math.min(minY, z.y);
      maxX = Math.max(maxX, z.x + (z.width || 250));
      maxY = Math.max(maxY, z.y + (z.height || 400));
    });
    const contentW = maxX - minX + 80;
    const contentH = maxY - minY + 80;
    const newZoom = Math.min(rect.width / contentW, rect.height / contentH, 2);
    setPan({
      x: (rect.width - contentW * newZoom) / 2 - minX * newZoom + 40 * newZoom,
      y: (rect.height - contentH * newZoom) / 2 - minY * newZoom + 40 * newZoom
    });
    setZoom(newZoom);
  };

  // ============================================
  // UTILIDADES
  // ============================================

  const snapToGridValue = (value) => {
    return snapToGrid ? Math.round(value / gridSize) * gridSize : value;
  };

  const isExpired = (fechaVencimiento) => {
    if (!fechaVencimiento) return false;
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    return vencimiento < hoy;
  };

  const isNearExpiry = (fechaVencimiento, days = 30) => {
    if (!fechaVencimiento) return false;
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diffTime = vencimiento - hoy;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= days;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // ============================================
  // GESTIÓN DE CAPAS (Z-INDEX)
  // ============================================

  const moveZoneToFront = async (zonaId) => {
    const newMaxZ = maxZIndex + 1;
    
    // Actualizar en el estado local
    setZonas(prev => prev.map(z =>
      z.id === zonaId ? { ...z, zIndex: newMaxZ } : z
    ));
    
    setMaxZIndex(newMaxZ);
    
    // Actualizar en el backend
    try {
      await authFetch(`${API_URL}/warehouse/zonas/${zonaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ zIndex: newMaxZ })
      });
    } catch (error) {
      console.error('Error updating zIndex:', error);
    }
    
    setZoneMenuOpen(null);
  };

  const moveZoneToBack = async (zonaId) => {
    // Encontrar el zIndex mínimo actual
    const minZ = Math.min(...zonas.map(z => z.zIndex || 100));
    const newZ = minZ - 1;
    
    // Actualizar en el estado local
    setZonas(prev => prev.map(z =>
      z.id === zonaId ? { ...z, zIndex: newZ } : z
    ));
    
    // Actualizar en el backend
    try {
      await authFetch(`${API_URL}/warehouse/zonas/${zonaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ zIndex: newZ })
      });
    } catch (error) {
      console.error('Error updating zIndex:', error);
    }
    
    setZoneMenuOpen(null);
  };

  const moveZoneForward = async (zonaId) => {
    const currentZona = zonas.find(z => z.id === zonaId);
    if (!currentZona) return;
    
    // eslint-disable-next-line no-unused-vars
    const currentZ = currentZona.zIndex || 100;

    // Encontrar la zona inmediatamente por encima
    const zonasOrdenadas = [...zonas].sort((a, b) => (a.zIndex || 100) - (b.zIndex || 100));
    const currentIndex = zonasOrdenadas.findIndex(z => z.id === zonaId);
    
    if (currentIndex < zonasOrdenadas.length - 1) {
      const zonaEncima = zonasOrdenadas[currentIndex + 1];
      const newZ = zonaEncima.zIndex + 1;
      
      // Si llegamos al máximo, necesitamos incrementar maxZIndex
      if (newZ > maxZIndex) {
        setMaxZIndex(newZ);
      }
      
      setZonas(prev => prev.map(z =>
        z.id === zonaId ? { ...z, zIndex: newZ } : z
      ));
      
      try {
        await authFetch(`${API_URL}/warehouse/zonas/${zonaId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ zIndex: newZ })
        });
      } catch (error) {
        console.error('Error updating zIndex:', error);
      }
    }
    
    setZoneMenuOpen(null);
  };

  const moveZoneBackward = async (zonaId) => {
    const currentZona = zonas.find(z => z.id === zonaId);
    if (!currentZona) return;
    
    // eslint-disable-next-line no-unused-vars
    const currentZ = currentZona.zIndex || 100;

    // Encontrar la zona inmediatamente por debajo
    const zonasOrdenadas = [...zonas].sort((a, b) => (a.zIndex || 100) - (b.zIndex || 100));
    const currentIndex = zonasOrdenadas.findIndex(z => z.id === zonaId);
    
    if (currentIndex > 0) {
      const zonaDebajo = zonasOrdenadas[currentIndex - 1];
      const newZ = zonaDebajo.zIndex - 1;
      
      setZonas(prev => prev.map(z =>
        z.id === zonaId ? { ...z, zIndex: newZ } : z
      ));
      
      try {
        await authFetch(`${API_URL}/warehouse/zonas/${zonaId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ zIndex: newZ })
        });
      } catch (error) {
        console.error('Error updating zIndex:', error);
      }
    }
    
    setZoneMenuOpen(null);
  };

  // ============================================
  // API CALLS
  // ============================================

  const updateZona = async (zonaId, updates) => {
    try {
      await authFetch(`${API_URL}/warehouse/zonas/${zonaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteZona = async (zonaId) => {
    if (!window.confirm('¿Eliminar esta zona?')) return;
    try {
      const response = await authFetch(`${API_URL}/warehouse/zonas/${zonaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        refrescarVista();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateLinea = async (lineaId, updates) => {
    try {
      await authFetch(`${API_URL}/warehouse/lineas/${lineaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteLinea = async (lineaId) => {
    if (!window.confirm('¿Eliminar esta línea?')) return;
    try {
      const response = await authFetch(`${API_URL}/warehouse/lineas/${lineaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        refrescarVista();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateColumna = async (columnaId, updates) => {
    try {
      await authFetch(`${API_URL}/warehouse/columnas/${columnaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteColumna = async (columnaId) => {
    if (!window.confirm('¿Eliminar esta columna?')) return;
    try {
      const response = await authFetch(`${API_URL}/warehouse/columnas/${columnaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        refrescarVista();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchColumnaInfo = async (columnaId) => {
    try {
      const response = await authFetch(`${API_URL}/warehouse/columnas/${columnaId}/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setColumnaInfo(result.data);
        setShowColumnaModal(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const addNivelToColumna = async (columnaId, tipo) => {
    try {
      const response = await authFetch(`${API_URL}/warehouse/columnas/${columnaId}/niveles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tipo })
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(result.message);
        fetchColumnaInfo(columnaId);
      } else {
        alert(result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al añadir nivel');
    }
  };

  const deleteNivel = async (nivelId) => {
    if (!window.confirm('¿Eliminar este nivel? Solo se puede si está vacío.')) return;
    try {
      const response = await authFetch(`${API_URL}/warehouse/niveles/${nivelId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(result.message);
        fetchColumnaInfo(columnaInfo.columna.id);
      } else {
        alert(result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar nivel');
    }
  };

  const moveProductUnits = async (moveData) => {
    try {
      const response = await authFetch(`${API_URL}/warehouse/lotes/mover-unidades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(moveData)
      });
      if (!response) return;

      const result = await response.json();
      if (result.success) {
        setShowMoveProductModal(false);
        const columnaIdToRefresh = columnaInfo?.columna?.id;
        await refrescarVista();
        if (columnaIdToRefresh) {
          fetchColumnaInfo(columnaIdToRefresh);
        }
      } else {
        alert(result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al mover unidades');
    }
  };

  // ============================================
  // DRAG & DROP Y RESIZE
  // ============================================

  const handleMouseDown = (e, element, elementType, parentId = null, lineaId = null, isResize = false, resizeHandle = null) => {
    e.stopPropagation();
    e.preventDefault();

    const canvasCoords = screenToCanvas(e.clientX, e.clientY);
    const x = canvasCoords.x;
    const y = canvasCoords.y;

    // Al hacer click en una zona, traerla al frente automáticamente
    if (elementType === 'zona' && !isResize) {
      const newZIndex = maxZIndex + 1;
      setMaxZIndex(newZIndex);
      setZonas(prev => prev.map(z =>
        z.id === element.id ? { ...z, zIndex: newZIndex } : z
      ));

      // Actualizar en el backend
      updateZona(element.id, { zIndex: newZIndex });
    }

    setDragState({
      isDragging: !isResize,
      isResizing: isResize,
      element,
      elementType,
      parentId,
      lineaId,
      startX: x,
      startY: y,
      startWidth: element.width || 45,
      startHeight: element.height || 45,
      offsetX: isResize ? 0 : x - (elementType === 'zona' ? element.x : (element.x || 0)),
      offsetY: isResize ? 0 : y - (elementType === 'zona' ? element.y : (element.y || 0)),
      resizeHandle
    });

    setSelectedElement({
      ...element,
      type: elementType,
      parentId,
      lineaId
    });
  };

  const handleMouseMove = (e) => {
    // Handle panning
    if (isPanning) {
      e.preventDefault();
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPan(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }

    if (!dragState.isDragging && !dragState.isResizing) return;
    e.preventDefault();
    if (!canvasRef.current) return;

    const canvasCoords = screenToCanvas(e.clientX, e.clientY);
    const mouseX = canvasCoords.x;
    const mouseY = canvasCoords.y;

    if (dragState.isDragging) {
      if (dragState.elementType === 'zona') {
        const newX = snapToGridValue(mouseX - dragState.offsetX);
        const newY = snapToGridValue(mouseY - dragState.offsetY);

        setZonas(prev => prev.map(z =>
          z.id === dragState.element.id ? { ...z, x: newX, y: newY } : z
        ));
      }
      else if (dragState.elementType === 'linea') {
        const zona = zonas.find(z => z.id === dragState.parentId);
        if (!zona) return;

        const newX = snapToGridValue(mouseX - dragState.offsetX - zona.x);
        const newY = snapToGridValue(mouseY - dragState.offsetY - zona.y);

        setZonas(prev => prev.map(z => {
          if (z.id === dragState.parentId) {
            return {
              ...z,
              lineas: z.lineas.map(c =>
                c.id === dragState.element.id ? { ...c, x: newX, y: newY } : c
              )
            };
          }
          return z;
        }));
      }
      else if (dragState.elementType === 'columna') {
        const zona = zonas.find(z => z.lineas?.some(c => c.id === dragState.lineaId));
        if (!zona) return;

        const linea = zona.lineas.find(c => c.id === dragState.lineaId);
        if (!linea) return;

        const lineaAbsX = zona.x + linea.x;
        const lineaAbsY = zona.y + linea.y;

        let newX = snapToGridValue(mouseX - dragState.offsetX - lineaAbsX);
        let newY = snapToGridValue(mouseY - dragState.offsetY - lineaAbsY);

        const headerHeight = 30;
        const minX = -dragState.element.width + 10;
        const maxX = linea.width - 10;
        const minY = headerHeight;
        const maxY = linea.height * 5;

        newX = Math.max(minX, Math.min(newX, maxX));
        newY = Math.max(minY, Math.min(newY, maxY));

        setZonas(prev => prev.map(z => ({
          ...z,
          lineas: z.lineas?.map(c => {
            if (c.id === dragState.lineaId) {
              return {
                ...c,
                columnas: c.columnas?.map(r =>
                  r.id === dragState.element.id ? { ...r, x: newX, y: newY } : r
                )
              };
            }
            return c;
          })
        })));
      }
    }
    else if (dragState.isResizing) {
      const deltaX = mouseX - dragState.startX;
      const deltaY = mouseY - dragState.startY;

      let newWidth = dragState.startWidth;
      let newHeight = dragState.startHeight;
      let newX = dragState.element.x;
      let newY = dragState.element.y;

      const handle = dragState.resizeHandle;
      const minSize = 20;

      if (handle.includes('e')) {
        newWidth = snapToGridValue(Math.max(minSize, dragState.startWidth + deltaX));
      }
      if (handle.includes('w')) {
        newWidth = snapToGridValue(Math.max(minSize, dragState.startWidth - deltaX));
        newX = snapToGridValue(dragState.element.x + deltaX);
      }
      if (handle.includes('s')) {
        newHeight = snapToGridValue(Math.max(minSize, dragState.startHeight + deltaY));
      }
      if (handle.includes('n')) {
        newHeight = snapToGridValue(Math.max(minSize, dragState.startHeight - deltaY));
        newY = snapToGridValue(dragState.element.y + deltaY);
      }

      if (dragState.elementType === 'zona') {
        setZonas(prev => prev.map(z =>
          z.id === dragState.element.id
            ? { ...z, x: newX, y: newY, width: newWidth, height: newHeight }
            : z
        ));
      }
      else if (dragState.elementType === 'linea') {
        setZonas(prev => prev.map(z => {
          if (z.id === dragState.parentId) {
            return {
              ...z,
              lineas: z.lineas.map(c =>
                c.id === dragState.element.id
                  ? { ...c, width: newWidth, height: newHeight }
                  : c
              )
            };
          }
          return z;
        }));
      }
      else if (dragState.elementType === 'columna') {
        setZonas(prev => prev.map(z => ({
          ...z,
          lineas: z.lineas?.map(c => {
            if (c.id === dragState.lineaId) {
              return {
                ...c,
                columnas: c.columnas?.map(r =>
                  r.id === dragState.element.id
                    ? { ...r, width: newWidth, height: newHeight }
                    : r
                )
              };
            }
            return c;
          })
        })));
      }
    }
  };

  const handleMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
      wasPanningRef.current = true;
      setTimeout(() => { wasPanningRef.current = false; }, 0);
      return;
    }

    if (dragState.element && (dragState.isDragging || dragState.isResizing)) {
      if (dragState.elementType === 'zona') {
        const zona = zonas.find(z => z.id === dragState.element.id);
        if (zona) {
          updateZona(zona.id, { x: zona.x, y: zona.y, width: zona.width, height: zona.height });
        }
      } else if (dragState.elementType === 'linea') {
        const zona = zonas.find(z => z.id === dragState.parentId);
        if (zona) {
          const linea = zona.lineas.find(c => c.id === dragState.element.id);
          if (linea) {
            updateLinea(linea.id, { x: linea.x, y: linea.y, width: linea.width, height: linea.height });
          }
        }
      } else if (dragState.elementType === 'columna') {
        const zona = zonas.find(z => z.lineas?.some(c => c.id === dragState.lineaId));
        if (zona) {
          const linea = zona.lineas.find(c => c.id === dragState.lineaId);
          if (linea) {
            const columna = linea.columnas.find(r => r.id === dragState.element.id);
            if (columna) {
              updateColumna(columna.id, { x: columna.x, y: columna.y, width: columna.width, height: columna.height });
            }
          }
        }
      }
    }

    setDragState({
      isDragging: false, isResizing: false, element: null, elementType: null,
      parentId: null, lineaId: null, startX: 0, startY: 0, startWidth: 0,
      startHeight: 0, offsetX: 0, offsetY: 0, resizeHandle: null
    });
  };

  const handleColumnaClick = (e, columnaId) => {
    e.stopPropagation();
    if (!dragState.isDragging) {
      fetchColumnaInfo(columnaId);
    }
  };

  const handleProductDragStart = (e, product, ubicacionId) => {
    setDraggingProduct({ product, ubicacionId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleProductDragEnd = () => {
    setDraggingProduct(null);
    setDropTargetNivel(null);
  };

  const handleNivelDragOver = (e, nivel, columna) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetNivel({ nivel, columna });
  };

  const handleNivelDragLeave = (e) => {
    e.stopPropagation();
    setDropTargetNivel(null);
  };

  const handleNivelDrop = (e, nivel, columna) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggingProduct) return;

    setMoveProductData({
      product: draggingProduct.product,
      ubicacion_origen_id: draggingProduct.ubicacionId,
      destino_nivel_id: nivel.id,
      destino_columna: columna,
      destino_nivel: nivel
    });
    setShowMoveProductModal(true);

    setDraggingProduct(null);
    setDropTargetNivel(null);
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderResizeHandles = (element, elementType, parentId = null, lineaId = null) => {
    if (selectedElement?.id !== element.id || selectedElement?.type !== elementType) {
      return null;
    }

    const handles = ['se', 'ne', 'sw', 'nw'];
    const handleSize = 10;

    return handles.map(handle => {
      let style = {
        position: 'absolute',
        width: `${handleSize}px`,
        height: `${handleSize}px`,
        background: '#2563eb',
        border: '2px solid white',
        borderRadius: '50%',
        cursor: `${handle}-resize`,
        zIndex: 10000
      };

      switch (handle) {
        case 'nw':
          style = { ...style, top: -handleSize / 2, left: -handleSize / 2 };
          break;
        case 'ne':
          style = { ...style, top: -handleSize / 2, right: -handleSize / 2 };
          break;
        case 'sw':
          style = { ...style, bottom: -handleSize / 2, left: -handleSize / 2 };
          break;
        case 'se':
          style = { ...style, bottom: -handleSize / 2, right: -handleSize / 2 };
          break;
        default:
          break;
      }

      return (
        <div
          key={handle}
          style={style}
          onMouseDown={(e) => handleMouseDown(e, element, elementType, parentId, lineaId, true, handle)}
        />
      );
    });
  };

  // Posicion helper: extracts P01/P02 from codigo_ubicacion
  const getPosicionFromCodigo = (codigoUbicacion) => {
    if (!codigoUbicacion) return null;
    const match = codigoUbicacion.match(/(P\d{2})$/);
    return match ? match[1] : null;
  };

  const renderColumna = (columna, linea, zona) => {
    const isSelected = selectedElement?.id === columna.id && selectedElement?.type === 'columna';
    const columnaWidth = columna.width || 45;
    const columnaHeight = columna.height || 45;

    return (
      <div
        key={columna.id}
        style={{
          position: 'absolute',
          left: `${columna.x}px`,
          top: `${columna.y}px`,
          width: `${columnaWidth}px`,
          height: `${columnaHeight}px`,
          background: 'white',
          borderWidth: isSelected ? '2px' : '1px',
          borderStyle: 'solid',
          borderColor: isSelected ? '#2563eb' : '#cbd5e1',
          borderRadius: '3px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: '700',
          color: '#64748b',
          cursor: 'move',
          transition: 'all 0.2s',
          boxShadow: isSelected ? '0 2px 8px rgba(37,99,235,0.3)' : 'none'
        }}
        onMouseDown={(e) => handleMouseDown(e, columna, 'columna', zona.id, linea.id)}
        onClick={(e) => handleColumnaClick(e, columna.id)}
      >
        {renderResizeHandles(columna, 'columna', zona.id, linea.id)}
        <Box size={13} />
        <span style={{ marginTop: '2px' }}>C{columna.numero}</span>
        {isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteColumna(columna.id);
            }}
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              padding: '2px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Trash2 size={8} color="#ef4444" />
          </button>
        )}
      </div>
    );
  };

  const renderLinea = (linea, zona) => {
    const isSelected = selectedElement?.id === linea.id && selectedElement?.type === 'linea';
    const lineaWidth = linea.width || 150;
    const lineaHeight = linea.height || 200;

    return (
      <div
        key={linea.id}
        style={{
          position: 'absolute',
          left: `${linea.x || 0}px`,
          top: `${linea.y || 0}px`,
          width: `${lineaWidth}px`,
          height: `${lineaHeight}px`,
          background: 'rgba(248, 250, 252, 0.5)',
          borderWidth: isSelected ? '2px' : '1px',
          borderStyle: 'dashed',
          borderColor: isSelected ? '#2563eb' : '#cbd5e1',
          borderRadius: '4px',
          cursor: 'move'
        }}
        onMouseDown={(e) => handleMouseDown(e, linea, 'linea', zona.id)}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedElement({
            ...linea,
            type: 'linea',
            parentId: zona.id
          });
        }}
      >
        {renderResizeHandles(linea, 'linea', zona.id)}

        <div style={{
          padding: '5px 8px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '11px',
          fontWeight: '600',
          color: '#475569'
        }}>
          <span>LÍNEA {linea.numero}</span>
          <button
            style={{
              padding: '3px 5px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '2px',
              cursor: 'pointer',
              fontSize: '9px',
              color: '#ef4444'
            }}
            onClick={(e) => {
              e.stopPropagation();
              deleteLinea(linea.id);
            }}
          >
            <Trash2 size={9} />
          </button>
        </div>

        <div style={{
          position: 'relative',
          height: 'calc(100% - 30px)'
        }}>
          {linea.columnas?.map(columna => renderColumna(columna, linea, zona))}
        </div>
      </div>
    );
  };

  const renderZona = (zona) => {
    const isSelected = selectedElement?.id === zona.id && selectedElement?.type === 'zona';
    const zonaWidth = zona.width || 115;
    const zonaHeight = zona.height || 345;

    return (
      <div
        key={zona.id}
        style={{
          position: 'absolute',
          left: `${zona.x}px`,
          top: `${zona.y}px`,
          width: `${zonaWidth}px`,
          height: `${zonaHeight}px`,
          background: 'white',
          borderWidth: isSelected ? '2px' : '1px',
          borderStyle: 'solid',
          borderColor: isSelected ? zona.color : `${zona.color}80`,
          borderRadius: '6px',
          cursor: 'move',
          boxShadow: isSelected
            ? '0 4px 12px rgba(0,0,0,0.15)'
            : '0 2px 4px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.2s, border 0.2s',
          zIndex: zona.zIndex || 100
        }}
        onMouseDown={(e) => handleMouseDown(e, zona, 'zona')}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedElement({ ...zona, type: 'zona' });
        }}
      >
        {renderResizeHandles(zona, 'zona')}

        <div style={{
          padding: '8px 10px',
          background: `${zona.color}10`,
          borderBottom: `1px solid ${zona.color}20`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '12px',
          fontWeight: '600',
          borderRadius: '5px 5px 0 0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '10px',
              height: '10px',
              background: zona.color,
              borderRadius: '2px'
            }} />
            <span style={{ color: '#334155' }}>{zona.codigo}</span>
            <span style={{
              color: '#94a3b8',
              fontSize: '11px',
              fontWeight: '400'
            }}>{zona.nombre}</span>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {/* Menú de capas */}
            <div style={{ position: 'relative' }}>
              <button
                style={{
                  padding: '4px 7px',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setZoneMenuOpen(zoneMenuOpen === zona.id ? null : zona.id);
                }}
              >
                <MoreVertical size={14} />
              </button>

              {zoneMenuOpen === zona.id && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 10001,
                    minWidth: '160px',
                    overflow: 'hidden'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      moveZoneToFront(zona.id);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'white',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#334155'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <ArrowUp size={14} />
                    Traer al frente
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      moveZoneForward(zona.id);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'white',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#334155'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <ArrowUp size={14} />
                    Mover adelante
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      moveZoneBackward(zona.id);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'white',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#334155'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <ArrowDown size={14} />
                    Mover atrás
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      moveZoneToBack(zona.id);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'white',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#334155'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <ArrowDown size={14} />
                    Enviar al fondo
                  </button>
                </div>
              )}
            </div>

            <button
              style={{
                padding: '4px 10px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: '600'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setNewLinea({ ...newLinea, zona_id: zona.id });
                setShowCreateLineaModal(true);
              }}
            >
              <Plus size={11} />
              Línea
            </button>
            <button
              style={{
                padding: '4px 7px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '11px',
                color: '#ef4444'
              }}
              onClick={(e) => {
                e.stopPropagation();
                deleteZona(zona.id);
              }}
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>

        <div style={{
          position: 'relative',
          height: 'calc(100% - 40px)',
          padding: '10px',
          overflow: 'visible'
        }}>
          {zona.lineas && zona.lineas.length > 0 ? (
            zona.lineas.map(linea => renderLinea(linea, zona))
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#94a3b8',
              fontSize: '12px',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <MapPin size={24} />
              <span>Sin líneas</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            gap: '16px',
            fontSize: '13px',
            color: '#64748b'
          }}>
            <span>Zonas: {zonas.length}</span>
            <span>Líneas: {zonas.reduce((acc, z) => acc + (z.lineas?.length || 0), 0)}</span>
            <span>Columnas: {zonas.reduce((acc, z) => acc + z.lineas?.reduce((acc2, c) => acc2 + (c.columnas?.length || 0), 0), 0)}</span>
          </div>

          {selectedElement && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 12px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '13px'
            }}>
              <Maximize2 size={14} color="#2563eb" />
              <span>Seleccionado:</span>
              <strong>
                {selectedElement.type === 'zona' && `Zona ${selectedElement.codigo}`}
                {selectedElement.type === 'linea' && `Línea ${selectedElement.numero}`}
                {selectedElement.type === 'columna' && `Columna ${selectedElement.numero}`}
              </strong>
              <button
                onClick={() => setSelectedElement(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  color: '#64748b'
                }}
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        <div
          ref={canvasRef}
          style={{
            flex: 1,
            position: 'relative',
            margin: '20px',
            background: '#fafafa',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            overflow: 'hidden',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            cursor: isPanning
              ? 'grabbing'
              : dragState.isDragging
                ? 'move'
                : dragState.isResizing
                  ? 'nwse-resize'
                  : 'grab'
          }}
          onMouseDown={(e) => {
            // Middle mouse button, space+left click, or left click on empty canvas for panning
            if (e.button === 1 || e.button === 0) {
              e.preventDefault();
              setIsPanning(true);
              setPanStart({ x: e.clientX, y: e.clientY });
              return;
            }
            setZoneMenuOpen(null);
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={() => { if (!wasPanningRef.current) setZoneMenuOpen(null); }}
        >
          {/* Grid background layer - scales with zoom */}
          {snapToGrid && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `
                repeating-linear-gradient(0deg, #f1f5f9 0px, #f1f5f9 1px, transparent 1px, transparent ${gridSize}px),
                repeating-linear-gradient(90deg, #f1f5f9 0px, #f1f5f9 1px, transparent 1px, transparent ${gridSize}px)
              `,
              backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`,
              backgroundPosition: `${pan.x % (gridSize * zoom)}px ${pan.y % (gridSize * zoom)}px`,
              pointerEvents: 'none'
            }} />
          )}

          {/* Transformed content layer */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transformOrigin: '0 0',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            width: 0,
            height: 0,
            overflow: 'visible'
          }}>
            {zonas.length === 0 ? (
              <div style={{
                position: 'absolute',
                left: '50%',
                top: '200px',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                color: '#94a3b8',
                whiteSpace: 'nowrap'
              }}>
                <Warehouse size={64} />
                <h3 style={{
                  margin: '16px 0 8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#64748b'
                }}>
                  Canvas Vacío
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '13px'
                }}>
                  Crea tu primera zona para comenzar
                </p>
              </div>
            ) : (
              zonas.map(zona => renderZona(zona))
            )}
          </div>

          {/* Zoom Controls - fixed position overlay */}
          <div style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            zIndex: 1000
          }}>
            <button
              onClick={zoomIn}
              style={{
                width: '36px',
                height: '36px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              title="Acercar"
            >
              <ZoomIn size={18} color="#475569" />
            </button>
            <div style={{
              width: '36px',
              height: '28px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: '600',
              color: '#475569',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              userSelect: 'none'
            }}
              onClick={resetView}
              title="Resetear zoom"
            >
              {Math.round(zoom * 100)}%
            </div>
            <button
              onClick={zoomOut}
              style={{
                width: '36px',
                height: '36px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              title="Alejar"
            >
              <ZoomOut size={18} color="#475569" />
            </button>
            <button
              onClick={fitToContent}
              style={{
                width: '36px',
                height: '36px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              title="Ajustar al contenido"
            >
              <Minimize2 size={16} color="#475569" />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL: Columna Info */}
      {showColumnaModal && columnaInfo && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowColumnaModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '6px',
              width: '90%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: 'white',
              zIndex: 10
            }}>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  Columna {columnaInfo.columna.numero}
                </h3>
                <p style={{
                  margin: '4px 0 0',
                  fontSize: '12px',
                  color: '#64748b'
                }}>
                  {columnaInfo.columna.lineas.zonas.codigo} → Línea {columnaInfo.columna.lineas.numero}
                </p>
              </div>
              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                <button
                  onClick={() => addNivelToColumna(columnaInfo.columna.id, 'almacenaje')}
                  style={{
                    padding: '6px 12px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <PlusCircle size={14} />
                  + Almacenaje
                </button>
                <button
                  onClick={() => addNivelToColumna(columnaInfo.columna.id, 'picking')}
                  style={{
                    padding: '6px 12px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <PlusCircle size={14} />
                  + Picking
                </button>
                <button
                  onClick={() => setShowColumnaModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: '#64748b'
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{
                  padding: '14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px'
                }}>
                  <div style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1e293b'
                  }}>
                    {columnaInfo.resumen.niveles_totales}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginTop: '4px'
                  }}>
                    Niveles
                  </div>
                </div>

                <div style={{
                  padding: '14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px'
                }}>
                  <div style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1e293b'
                  }}>
                    {columnaInfo.resumen.niveles_ocupados}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginTop: '4px'
                  }}>
                    Ocupados
                  </div>
                </div>

                <div style={{
                  padding: '14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px'
                }}>
                  <div style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1e293b'
                  }}>
                    {columnaInfo.resumen.total_lotes}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginTop: '4px'
                  }}>
                    Productos
                  </div>
                </div>

                <div style={{
                  padding: '14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px'
                }}>
                  <div style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1e293b'
                  }}>
                    {columnaInfo.resumen.total_unidades}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginTop: '4px'
                  }}>
                    Unidades
                  </div>
                </div>
              </div>

              <div>
                <h4 style={{
                  margin: '0 0 14px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e293b'
                }}>
                  Niveles de la Columna
                </h4>

                {columnaInfo.niveles.sort((a, b) => a.numero - b.numero).map(nivel => (
                  <div
                    key={nivel.id}
                    style={{
                      marginBottom: '12px',
                      borderWidth: dropTargetNivel?.nivel?.id === nivel.id ? '2px' : '1px',
                      borderStyle: dropTargetNivel?.nivel?.id === nivel.id ? 'dashed' : 'solid',
                      borderColor: dropTargetNivel?.nivel?.id === nivel.id ? '#2563eb' : '#e2e8f0',
                      borderLeftWidth: '3px',
                      borderLeftColor: nivel.tipo === 'picking' ? '#10b981' : '#2563eb',
                      borderRadius: '4px',
                      background: dropTargetNivel?.nivel?.id === nivel.id ? '#eff6ff' : 'white',
                      transition: 'all 0.2s'
                    }}
                    onDragOver={(e) => handleNivelDragOver(e, nivel, columnaInfo.columna)}
                    onDragLeave={handleNivelDragLeave}
                    onDrop={(e) => handleNivelDrop(e, nivel, columnaInfo.columna)}
                  >
                    <div style={{
                      padding: '12px 14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: '#f8fafc'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Layers size={14} />
                        <strong style={{ fontSize: '13px' }}>
                          Nivel {nivel.numero}
                        </strong>
                        <span style={{
                          padding: '2px 8px',
                          background: nivel.tipo === 'picking' ? '#d1fae5' : '#dbeafe',
                          color: nivel.tipo === 'picking' ? '#065f46' : '#1e40af',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          {nivel.tipo.toUpperCase()}
                        </span>
                      </div>

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          fontSize: '12px',
                          color: '#64748b'
                        }}>
                          {nivel.ocupado ? (
                            <span>
                              {nivel.total_lotes} productos • {nivel.total_unidades} uds
                            </span>
                          ) : (
                            <span style={{ color: '#10b981' }}>
                              ✓ Disponible
                            </span>
                          )}
                        </div>

                        {!nivel.ocupado && (
                          <button
                            onClick={() => deleteNivel(nivel.id)}
                            style={{
                              padding: '4px',
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              color: '#ef4444'
                            }}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* P01/P02 Position Indicators */}
                    <div style={{
                      padding: '8px 14px',
                      display: 'flex',
                      gap: '8px',
                      borderBottom: nivel.productos && nivel.productos.length > 0 ? '1px solid #e2e8f0' : 'none'
                    }}>
                      {['P01', 'P02'].map(pos => {
                        const prodsInPos = (nivel.productos || []).filter(p => getPosicionFromCodigo(p.codigo_ubicacion) === pos);
                        const hasProducts = prodsInPos.length > 0;
                        return (
                          <div key={pos} style={{
                            flex: 1,
                            padding: '6px 10px',
                            background: hasProducts ? '#fef3c7' : '#f0fdf4',
                            border: `1px solid ${hasProducts ? '#fde68a' : '#bbf7d0'}`,
                            borderRadius: '4px',
                            textAlign: 'center',
                            fontSize: '11px'
                          }}>
                            <div style={{ fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                              {pos} {pos === 'P01' ? '(Der.)' : '(Izq.)'}
                            </div>
                            <div style={{
                              color: hasProducts ? '#92400e' : '#15803d',
                              fontWeight: '600',
                              fontSize: '10px'
                            }}>
                              {hasProducts ? `${prodsInPos.length} prod` : 'Libre'}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {nivel.productos && nivel.productos.length > 0 && (
                      <div style={{ padding: '12px 14px' }}>
                        {/* Group products by position */}
                        {['P01', 'P02'].map(pos => {
                          const prodsInPos = nivel.productos.filter(p => getPosicionFromCodigo(p.codigo_ubicacion) === pos);
                          const unassigned = pos === 'P01' ? nivel.productos.filter(p => !getPosicionFromCodigo(p.codigo_ubicacion)) : [];
                          const allProds = [...prodsInPos, ...(pos === 'P01' ? unassigned : [])];
                          if (allProds.length === 0) return null;
                          return (
                            <div key={pos} style={{ marginBottom: '10px' }}>
                              <div style={{
                                fontSize: '11px',
                                fontWeight: '700',
                                color: '#475569',
                                marginBottom: '6px',
                                padding: '3px 8px',
                                background: '#f1f5f9',
                                borderRadius: '3px',
                                display: 'inline-block'
                              }}>
                                {pos} {pos === 'P01' ? '(Derecha)' : '(Izquierda)'}
                              </div>
                              {allProds.map((prod, idx) => {
                          const expired = isExpired(prod.fecha_vencimiento);
                          const nearExpiry = !expired && isNearExpiry(prod.fecha_vencimiento);

                          return (
                            <div
                              key={`${prod.barcode}-${prod.ubicacion_id}-${idx}`}
                              draggable
                              onDragStart={(e) => handleProductDragStart(e, prod, prod.ubicacion_id)}
                              onDragEnd={handleProductDragEnd}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px',
                                background: expired ? '#fef2f2' : nearExpiry ? '#fffbeb' : '#f8fafc',
                                border: `1px solid ${expired ? '#fecaca' : nearExpiry ? '#fde68a' : '#e2e8f0'}`,
                                borderRadius: '4px',
                                marginBottom: idx < nivel.productos.length - 1 ? '8px' : 0,
                                cursor: 'move',
                                transition: 'all 0.2s',
                                position: 'relative'
                              }}
                            >
                              {(expired || nearExpiry) && (
                                <div style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  background: expired ? '#ef4444' : '#f59e0b',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '20px',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px'
                                }}>
                                  {expired ? <AlertTriangle size={12} /> : <AlertCircle size={12} />}
                                </div>
                              )}

                              {prod.imagen_url && (
                                <img
                                  src={prod.imagen_url}
                                  alt={prod.descripcion}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '1px solid #e2e8f0'
                                  }}
                                />
                              )}

                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: '#1e293b',
                                  marginBottom: '4px'
                                }}>
                                  {prod.descripcion}
                                </div>

                                <div style={{
                                  fontSize: '11px',
                                  color: '#64748b',
                                  fontFamily: 'monospace',
                                  marginBottom: '4px'
                                }}>
                                  {prod.barcode} • Lote {prod.lote}
                                </div>

                                <div style={{
                                  fontSize: '11px',
                                  color: expired ? '#dc2626' : nearExpiry ? '#ea580c' : '#64748b',
                                  marginBottom: '4px',
                                  fontWeight: expired || nearExpiry ? '600' : '400'
                                }}>
                                  Vence: {formatDate(prod.fecha_vencimiento)}
                                </div>

                                <div style={{
                                  fontSize: '11px',
                                  color: '#64748b',
                                  display: 'flex',
                                  gap: '8px',
                                  flexWrap: 'wrap'
                                }}>
                                  <span style={{
                                    padding: '2px 6px',
                                    background: '#dbeafe',
                                    borderRadius: '3px'
                                  }}>
                                    {prod.cantidad_max || 0} {prod.unidad_max}
                                  </span>
                                  <span style={{
                                    padding: '2px 6px',
                                    background: '#dbeafe',
                                    borderRadius: '3px'
                                  }}>
                                    {prod.cantidad_med || 0} {prod.unidad_med}
                                  </span>
                                  <span style={{
                                    padding: '2px 6px',
                                    background: '#dbeafe',
                                    borderRadius: '3px'
                                  }}>
                                    {prod.cantidad_peq || 0} {prod.unidad_peq}
                                  </span>
                                  <span style={{
                                    padding: '2px 6px',
                                    background: '#f0fdf4',
                                    borderRadius: '3px',
                                    color: '#15803d'
                                  }}>
                                    {prod.stock_actual || 0} uds
                                  </span>
                                </div>
                              </div>

                              <button
                                onClick={() => {
                                  setMoveProductData({
                                    product: prod,
                                    ubicacion_origen_id: prod.ubicacion_id,
                                    destino_nivel_id: null,
                                    destino_columna: null,
                                    destino_nivel: null
                                  });
                                  setShowMoveProductModal(true);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#2563eb',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <Move size={12} />
                                Mover
                              </button>
                            </div>
                          );
                        })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Mover Producto */}
      {showMoveProductModal && moveProductData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
          onClick={() => setShowMoveProductModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '6px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              zIndex: 1002
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '600'
              }}>
                Mover Producto
              </h3>
              <button
                onClick={() => setShowMoveProductModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#64748b'
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{
                padding: '16px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '8px'
                }}>
                  {moveProductData.product.descripcion}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginBottom: '4px'
                }}>
                  Barcode: {moveProductData.product.barcode}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginBottom: '4px'
                }}>
                  Lote: {moveProductData.product.lote}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#64748b'
                }}>
                  Ubicación actual: {moveProductData.product.codigo_ubicacion}
                </div>

                {moveProductData.destino_nivel && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    background: '#dbeafe',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <ArrowRight size={14} />
                    Destino: Nivel {moveProductData.destino_nivel.numero} (Columna {moveProductData.destino_columna.numero})
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Especifica qué mover:
                </label>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '10px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '11px',
                      color: '#64748b',
                      marginBottom: '4px'
                    }}>
                      {moveProductData.product.unidad_max}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={moveProductData.product.cantidad_max || 0}
                      defaultValue={0}
                      id="move-cajas"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{
                      fontSize: '10px',
                      color: '#94a3b8'
                    }}>
                      Máx: {moveProductData.product.cantidad_max || 0}
                    </span>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '11px',
                      color: '#64748b',
                      marginBottom: '4px'
                    }}>
                      {moveProductData.product.unidad_med}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={moveProductData.product.cantidad_med || 0}
                      defaultValue={0}
                      id="move-displays"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{
                      fontSize: '10px',
                      color: '#94a3b8'
                    }}>
                      Máx: {moveProductData.product.cantidad_med || 0}
                    </span>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '11px',
                      color: '#64748b',
                      marginBottom: '4px'
                    }}>
                      {moveProductData.product.unidad_peq}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={moveProductData.product.cantidad_peq || 0}
                      defaultValue={0}
                      id="move-unidades"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{
                      fontSize: '10px',
                      color: '#94a3b8'
                    }}>
                      Máx: {moveProductData.product.cantidad_peq || 0}
                    </span>
                  </div>
                </div>
              </div>

              {!moveProductData.destino_nivel_id && (
                <div style={{
                  padding: '12px',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={16} />
                  <span>Arrastra el producto a un nivel destino</span>
                </div>
              )}
            </div>

            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              background: '#f8fafc'
            }}>
              <button
                onClick={() => setShowMoveProductModal(false)}
                style={{
                  padding: '8px 14px',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Cancelar
              </button>

              <button
                onClick={() => {
                  const cajas = parseInt(document.getElementById('move-cajas').value) || 0;
                  const displays = parseInt(document.getElementById('move-displays').value) || 0;
                  const unidades = parseInt(document.getElementById('move-unidades').value) || 0;

                  if (cajas === 0 && displays === 0 && unidades === 0) {
                    alert('Debes especificar al menos una unidad');
                    return;
                  }

                  if (!moveProductData.destino_nivel_id) {
                    alert('Debes seleccionar un nivel destino');
                    return;
                  }

                  moveProductUnits({
                    barcode: moveProductData.product.barcode,
                    lote_numero: moveProductData.product.lote,
                    ubicacion_origen_id: moveProductData.ubicacion_origen_id,
                    destino_nivel_id: moveProductData.destino_nivel_id,
                    cantidad_cajas: cajas,
                    cantidad_displays: displays,
                    cantidad_unidades: unidades
                  });
                }}
                style={{
                  padding: '8px 14px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <ArrowRight size={14} />
                Mover
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WarehouseCanvas;